/**
 * @fileoverview Hook for cached local-first lookup reads supplied by the host.
 * @module ui-admin/hooks/useLookupCache
 *
 * Responsibilities:
 * - Cache lookup reads by reader and lookup type and deduplicate concurrent requests.
 * - Allow explicit readers to override the current host reader.
 *
 * Dependencies: React, LookupDataSourceContext, and shared lookup contracts.
 * Architecture: Host-owned local-first reader is injected; no persistence access.
 * Notes: Missing readers safely return an empty result rather than fabricated data.
 */
import { useCallback } from 'react';
import { useLookupDataSource } from '../contexts/LookupDataSourceContext';
import type { LookupValue } from '../services/lookupService';

/**
 * @contract LookupReader
 * Purpose: Defines the local lookup query accepted by useLookupCache.
 * Fields: lookupType argument and Promise of lookup values.
 * Rules: The optional explicit reader overrides the injected host reader.
 * Sync: Reads the host's local-first lookup state.
 */
export type LookupReader = (lookupType: string) => Promise<LookupValue[]>;
interface CacheEntry { values: LookupValue[]; fetchedAt: number }
const TTL_MS = 5 * 60 * 1000;
const cacheByReader = new WeakMap<LookupReader, Map<string, CacheEntry>>();
const inflightByReader = new WeakMap<LookupReader, Map<string, Promise<LookupValue[]>>>();

/**
 * @hook useLookupCache
 * Purpose: Provide TTL-cached lookup values without coupling shared UI to storage.
 * Returns: getLookupValues and invalidate helpers.
 * Side Effects: Calls the explicit or context-provided host reader on cache misses.
 * Offline Behavior: Uses host-local cached data; returns [] if no reader is supplied.
 */
export function useLookupCache(reader?: LookupReader) {
  const contextReader = useLookupDataSource()?.listLookupValues;
  const activeReader = reader ?? contextReader;
  const getLookupValues = useCallback(async (lookupType: string): Promise<LookupValue[]> => {
    if (!activeReader) return [];
    let cache = cacheByReader.get(activeReader);
    if (!cache) {
      cache = new Map();
      cacheByReader.set(activeReader, cache);
    }
    const entry = cache.get(lookupType);
    if (entry && Date.now() - entry.fetchedAt < TTL_MS) return entry.values;
    let inflight = inflightByReader.get(activeReader);
    if (!inflight) {
      inflight = new Map();
      inflightByReader.set(activeReader, inflight);
    }
    if (inflight.has(lookupType)) return inflight.get(lookupType)!;
    const promise = activeReader(lookupType).then((values) => {
      cache!.set(lookupType, { values, fetchedAt: Date.now() });
      inflight.delete(lookupType);
      return values;
    }).catch((error: unknown) => {
      inflight.delete(lookupType);
      throw error;
    });
    inflight.set(lookupType, promise);
    return promise;
  }, [activeReader]);
  const invalidate = useCallback((lookupType?: string) => {
    if (!activeReader) return;
    const cache = cacheByReader.get(activeReader);
    if (lookupType) cache?.delete(lookupType);
    else cache?.clear();
  }, [activeReader]);
  return { getLookupValues, invalidate };
}
